import React from "react";
import { Text } from "../../../atoms/Text/Text";
import { User } from "../../../models/User";
import classNames from "classnames";

import styles from "./FriendChallenge.css";
import { FriendTabs } from "../../../services/Friend/FriendService";
import {
  ChallengeModel,
  ChallengesReceivedModel,
  RespondChallengePayloadModel,
} from "../../../models/Challenge";
import ChallengeApiService from "../../../services/Friend/ChallengeApiService";
import ChallengeService, {
  ChallengeStates,
} from "../../../services/Friend/ChallengeService";
import moment, { Moment } from "moment";

type RenderReceivedChallengesByDateProps = {
  data: ChallengesReceivedModel;
  user: User;
  tabId: FriendTabs;
  isOpened: boolean;
  onTabSelect?: (tab: FriendTabs, gameId?: number) => void;
  onRespondToChallenge: (p: RespondChallengePayloadModel) => Promise<any>;
  onDismissNotification: (challengeId) => Promise<void>;
};

export class RenderReceivedChallengesByDate extends React.Component<RenderReceivedChallengesByDateProps> {
  state = {
    activeAccordion: undefined,
    activeViewMore: false,
    wagerScore: 0,
    dismissedNotifications: [],
  };

  componentDidMount() {
    this.loadDateWagerSumup();
  }

  formatDate(date) {
    return moment.utc(date).local().format("DD/MM/YY hh:mm A");
  }

  getExpirationDiffDuration(expirationMoment: Moment) {
    const duration = moment.duration(expirationMoment.diff(moment.utc()));
    return `${duration.hours()}hrs ${duration.minutes()}m`;
  }

  async loadDateWagerSumup() {
    return ChallengeApiService.getWagerSumup(
      this.props.data.gameInfo.puzzleId
    ).then((wagerScore) => {
      this.setState({ wagerScore });
    });
  }

  handleViewMore = () => {
    this.setState({
      activeViewMore: !this.state.activeViewMore,
    });
  };

  handleToggle = (challengeId, dismissNotification) => {
    this.setState({
      activeAccordion:
        this.state.activeAccordion === challengeId ? undefined : challengeId,
    });
    if (dismissNotification) {
      this.props.onDismissNotification(challengeId).then(() => {
        this.setState({
          dismissedNotifications: [
            ...this.state.dismissedNotifications,
            challengeId,
          ],
        });
      });
    }
  };

  handleRespondToChallenge(
    gameId: number,
    friendCode: string,
    accept: boolean
  ) {
    this.props.onRespondToChallenge({
      friendCode,
      gameId,
      answer: accept ? ChallengeStates.approved : ChallengeStates.rejected,
    });
  }

  renderChallengeResult(challenge: ChallengeModel) {
    const { challengeStatus, friendScore, userScore, friendName, wagerScore } =
      challenge;
    const isUserWinner = challengeStatus === ChallengeStates.win;
    const isDraw = challengeStatus === ChallengeStates.draw;
    const isPending = challengeStatus === ChallengeStates.approved;

    return (
      <table className={styles.challenges_results_table}>
        <thead>
          <tr>
            <th>Player</th>
            <th>Score</th>
            <th>Wager</th>
            <th>
              <span className={styles.hidden_mobile}>Final Game Pts.</span>
              <span className={styles.hidden_desktop}>Final Pts.</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className={classNames({ [styles.winner_row]: isUserWinner })}>
            <td>{this.props.user.name}</td>
            <td>{challenge.userScore || "--"}</td>
            <td>
              <span>
                {isDraw || isPending
                  ? "--"
                  : isUserWinner
                  ? `+${challenge.wagerScore}`
                  : `-${challenge.wagerScore}`}
              </span>
            </td>
            <td>
              {ChallengeService.calculateFinalPoints(
                challengeStatus,
                userScore,
                wagerScore
              ) || "--"}
            </td>
          </tr>

          <tr
            className={classNames({
              [styles.winner_row]: !isUserWinner && !isDraw && !isPending,
            })}
          >
            <td>{friendName}</td>
            <td>{friendScore || "--"}</td>
            <td>
              <span>
                {isDraw || isPending
                  ? "--"
                  : isUserWinner
                  ? `-${challenge.wagerScore}`
                  : `+${challenge.wagerScore}`}
              </span>
            </td>
            <td>
              {isPending
                ? "--"
                : ChallengeService.calculateFinalPoints(
                    isDraw
                      ? ChallengeStates.draw
                      : isUserWinner
                      ? ChallengeStates.lose
                      : ChallengeStates.win,
                    friendScore,
                    wagerScore
                  ) || "--"}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  renderAcceptedChallenges(challenges: ChallengeModel[]) {
    const { activeAccordion, dismissedNotifications } = this.state;
    const {
      data: { gameInfo },
    } = this.props;

    return (
      challenges.length > 0 && (
        <>
          <Text size="xs" className={styles.challenge_section_header}>
            Accepted Challenges
          </Text>
          <Accordion>
            {challenges.map((record, i) => (
              <AccordionItem
                key={i}
                active={activeAccordion === record.challengeId}
              >
                <AccordionHeader
                  className={
                    record.hasUserActivity &&
                    !dismissedNotifications.includes(record.challengeId) &&
                    styles.show_notification_badge
                  }
                  onClick={() =>
                    this.handleToggle(
                      record.challengeId,
                      record.hasUserActivity
                    )
                  }
                >
                  <span>{record.friendName}</span>
                  <span>
                    <img width="10" src="/icons/check-icon.png" />
                  </span>
                  <span>{record.wagerScore}</span>
                  
                    {record.challengeStatus === ChallengeStates.approved ? (
                      <span>
                        Play within{" "}
                        {this.getExpirationDiffDuration(
                          ChallengeService.getExpirationDate(
                            record.challengeDate,
                            record.expirationDate,
                            gameInfo.date
                          )
                        )}
                      </span>
                    ) : (
                      <span className={classNames({[styles.winner_status]: record.challengeStatus === ChallengeStates.win})}>
                        {ChallengeService.getChallengeStatusMessage(
                          record.challengeStatus
                        )}
                      </span>
                    )}
                </AccordionHeader>
                <AccordionBody>
                  {this.renderChallengeResult(record)}
                </AccordionBody>
              </AccordionItem>
            ))}
          </Accordion>
        </>
      )
    );
  }

  renderReceivedChallenges(challenges: ChallengeModel[]) {
    const { activeAccordion, dismissedNotifications } = this.state;
    const {
      data: { gameInfo },
    } = this.props;

    return (
      challenges.length > 0 && (
        <>
          <Text size="xs" className={styles.challenge_section_header}>
            Received Challenges
          </Text>
          <Accordion inverted>
            {challenges.map((record, i) => (
              <AccordionItem
                key={i}
                active={activeAccordion === record.challengeId}
              >
                <AccordionHeader
                  className={
                    record.hasUserActivity &&
                    !dismissedNotifications.includes(record.challengeId) &&
                    styles.show_notification_badge
                  }
                  onClick={() =>
                    this.handleToggle(
                      record.challengeId,
                      record.hasUserActivity
                    )
                  }
                >
                  <span>{record.friendName}</span>
                  <span>
                    <img width="10" src="/icons/check-icon.png" />
                  </span>
                  <span>{record.wagerScore}</span>
                  <div>
                    {record.challengeStatus !== ChallengeStates.pending ? (
                      ChallengeService.getChallengeStatusMessage(
                        record.challengeStatus
                      )
                    ) : (
                      <>
                        <button
                          className={styles.challenge_action_button}
                          type="button"
                          onClick={() =>
                            this.handleRespondToChallenge(
                              record.gameId,
                              record.friendCode,
                              true
                            )
                          }
                        >
                          Accept
                        </button>
                        <button
                          className={styles.challenge_action_button}
                          type="button"
                          onClick={() =>
                            this.handleRespondToChallenge(
                              record.gameId,
                              record.friendCode,
                              false
                            )
                          }
                        >
                          Decline
                        </button>
                        <Text as="div">
                          Play within{" "}
                          {this.getExpirationDiffDuration(
                            ChallengeService.getExpirationDate(
                              record.challengeDate,
                              record.expirationDate,
                              gameInfo.date
                            )
                          )}
                        </Text>
                      </>
                    )}
                  </div>
                </AccordionHeader>
                <AccordionBody>
                  <Text className={styles.challenge_info}>
                    <b>Received:</b> {this.formatDate(record.challengeDate)}
                  </Text>
                  <Text className={styles.challenge_info}>
                    <b>Expires: </b>
                    {this.formatDate(
                      ChallengeService.getExpirationDate(
                        record.challengeDate,
                        record.expirationDate,
                        gameInfo.date
                      )
                    )}
                  </Text>
                </AccordionBody>
              </AccordionItem>
            ))}
          </Accordion>
        </>
      )
    );
  }

  render() {
    const {
      onTabSelect,
      tabId,
      isOpened,
      data: { challengesReceived, gameInfo },
    } = this.props;
    const { activeViewMore, wagerScore } = this.state;

    const acceptedChallenges = [],
      receivedChallenges = [];

    const acceptedChallengesStatuses = [
      ChallengeStates.approved,
      ChallengeStates.win,
      ChallengeStates.lose,
      ChallengeStates.draw,
    ];

    challengesReceived.forEach((c) =>
      acceptedChallengesStatuses.includes(c.challengeStatus)
        ? acceptedChallenges.push(c)
        : receivedChallenges.push(c)
    );

    return (
      <div
        role="tabpanel"
        id={tabId}
        className={classNames({ [styles.hiddenFriendTable]: !isOpened })}
        aria-labelledby={`tab${tabId}`}
      >
        <BackButton
          onClick={() => onTabSelect(FriendTabs.ViewReceivedChallenges)}
        >
          Received Challenges
        </BackButton>
        <Text
          className={classNames(styles.challenge_cta, {
            [styles.open]: activeViewMore,
          })}
        >
          You have received Challenges, maybe even with some wagers, from your
          friends. They are marked with a red circle. There is no max on the
          number of Challenges and/or Wagers you can receive. So, play away and
          score big!
        </Text>
        <span
          className={classNames(styles.viewMore, {
            [styles.open]: activeViewMore,
          })}
          onClick={() => this.handleViewMore()}
        >
          View {activeViewMore ? "Less" : "More"}
        </span>

        <div className={styles.challenge_header}>
          {gameInfo && (
            <div>
              <h2 className={styles.game_date}>
                {ChallengeService.formatDateToLiteral(gameInfo.date)}
              </h2>
              <a
                href={
                  gameInfo.availableToPlay &&
                  `?toGo=game_${ChallengeService.formatDate(gameInfo.date)}`
                }
                className={classNames(styles.button, {
                  [styles.disabled]: !gameInfo.availableToPlay,
                })}
              >
                Play
              </a>
            </div>
          )}

          <ScoreCounter score={wagerScore} />
        </div>

        {acceptedChallenges.length === 0 && receivedChallenges.length === 0 && (
          <Text
            color="accentLight"
            align="center"
            as="div"
            role="status"
            ariaLive="polite"
          >
            You haven't received any challenges yet.
          </Text>
        )}

        {acceptedChallenges &&
          this.renderAcceptedChallenges(acceptedChallenges)}

        {receivedChallenges &&
          this.renderReceivedChallenges(receivedChallenges)}
      </div>
    );
  }
}

const BackButton = (props: any) => (
  <span className={styles.back_btn} {...props} />
);

const Accordion = (props: any) => (
  <ul
    className={classNames(styles.accordion, {
      [styles.inverted]: props.inverted,
    })}
    {...props}
  />
);
const AccordionItem = (props: any) => (
  <li
    className={classNames(styles.accordion_item, {
      [styles.active]: props.active,
    })}
    {...props}
  />
);
const AccordionHeader = ({ children, className, ...props }: any) => (
  <div className={classNames(styles.accordion_header, className)} {...props}>
    {children}
  </div>
);
const AccordionBody = (props: any) => (
  <div className={styles.accordion_body_wrapper}>
    <div className={styles.accordion_body}>{props.children}</div>
  </div>
);

const ScoreCounter = ({ score }: { score?: number }) => (
  <div className={styles.score_counter}>
    <Text className={styles.points}>{score}</Text>
    <Text>Wager pts. Won</Text>
  </div>
);

export default Accordion;
